// src/pages/admin/CommissionsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminCommissionsApi } from '../../api/services'
import { Button, Card, CardHeader, CardBody, PageSpinner } from '../../components/common/ui'
import { Percent, Save, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CommissionsPage() {
  const qc = useQueryClient()
  const [editingGlobal, setEditingGlobal] = useState(false)
  const [globalInput, setGlobalInput] = useState('')
  const [edits, setEdits] = useState({}) // { categoryId: rateValue }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-commissions'],
    queryFn: () => adminCommissionsApi.list().then(r => r.data),
  })

  const updateCategory = useMutation({
    mutationFn: ({ catId, rate }) => adminCommissionsApi.update(catId, { commission_rate: rate === '' ? null : parseFloat(rate) }),
    onSuccess: (_, { catId }) => {
      toast.success('Commission updated')
      qc.invalidateQueries(['admin-commissions'])
      setEdits(prev => { const n = { ...prev }; delete n[catId]; return n })
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })

  const updateGlobal = useMutation({
    mutationFn: () => adminCommissionsApi.updateGlobal({ rate: parseFloat(globalInput) }),
    onSuccess: () => {
      toast.success('Global rate updated')
      qc.invalidateQueries(['admin-commissions'])
      setEditingGlobal(false)
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })

  if (isLoading) return <PageSpinner />

  const categories = data?.data || []
  const globalRate = data?.global_rate ?? 8

  return (
    <div className="max-w-4xl">
      <h1 className="text-xl font-display font-bold mb-6">Commission Management</h1>

      {/* Global rate */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <span className="font-bold text-sm flex items-center gap-2"><Percent size={14} /> Global Default Rate</span>
            {!editingGlobal && (
              <button onClick={() => { setEditingGlobal(true); setGlobalInput(String(globalRate)) }}
                className="text-xs text-pink font-bold hover:underline">Edit</button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {editingGlobal ? (
            <div className="flex items-center gap-3">
              <input type="number" step="0.5" min="0" max="50" value={globalInput}
                onChange={e => setGlobalInput(e.target.value)}
                className="input w-24 h-9 text-sm text-center" />
              <span className="text-sm text-muted">%</span>
              <Button size="sm" onClick={() => updateGlobal.mutate()} loading={updateGlobal.isPending}><Save size={13} /> Save</Button>
              <Button size="sm" variant="secondary" onClick={() => setEditingGlobal(false)}>Cancel</Button>
            </div>
          ) : (
            <p className="text-2xl font-bold">{globalRate}%
              <span className="text-sm text-muted font-normal ml-2">applied to categories without a custom rate</span>
            </p>
          )}
        </CardBody>
      </Card>

      {/* Per-category rates */}
      <Card>
        <CardHeader><span className="font-bold text-sm">Per-Category Commission Rates</span></CardHeader>
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface">
                <th className="text-left px-4 py-3 font-bold text-xs text-muted uppercase">Category</th>
                <th className="text-center px-4 py-3 font-bold text-xs text-muted uppercase w-20">Products</th>
                <th className="text-center px-4 py-3 font-bold text-xs text-muted uppercase w-20">HSN</th>
                <th className="text-center px-4 py-3 font-bold text-xs text-muted uppercase w-20">GST</th>
                <th className="text-center px-4 py-3 font-bold text-xs text-muted uppercase w-36">Commission Rate</th>
                <th className="text-center px-4 py-3 font-bold text-xs text-muted uppercase w-24">Action</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => {
                const editing = cat.id in edits
                const value = editing ? edits[cat.id] : (cat.commission_rate ?? '')
                return (
                  <tr key={cat.id} className="border-b border-border last:border-0 hover:bg-surface/50">
                    <td className="px-4 py-3">
                      <span className="font-medium">{cat.name}</span>
                      {!cat.is_custom && <span className="text-[10px] text-muted ml-2">(global)</span>}
                    </td>
                    <td className="text-center px-4 py-3 text-muted">{cat.products_count}</td>
                    <td className="text-center px-4 py-3 text-muted font-mono text-xs">{cat.hsn_code || '—'}</td>
                    <td className="text-center px-4 py-3 text-muted">{cat.gst_rate ?? '—'}%</td>
                    <td className="text-center px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <input type="number" step="0.5" min="0" max="50"
                          value={value} placeholder={String(globalRate)}
                          onChange={e => setEdits(prev => ({ ...prev, [cat.id]: e.target.value }))}
                          className="input w-20 h-8 text-xs text-center" />
                        <span className="text-xs text-muted">%</span>
                      </div>
                    </td>
                    <td className="text-center px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {editing && (
                          <>
                            <button onClick={() => updateCategory.mutate({ catId: cat.id, rate: edits[cat.id] })}
                              className="text-green-600 hover:text-green-800 p-1" title="Save"><Save size={14} /></button>
                            <button onClick={() => setEdits(prev => { const n = { ...prev }; delete n[cat.id]; return n })}
                              className="text-gray-400 hover:text-gray-600 p-1" title="Reset"><RotateCcw size={14} /></button>
                          </>
                        )}
                        {cat.is_custom && !editing && (
                          <button onClick={() => updateCategory.mutate({ catId: cat.id, rate: '' })}
                            className="text-xs text-muted hover:text-red-500" title="Reset to global">Reset</button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {categories.length === 0 && <p className="text-sm text-muted text-center py-8">No categories found.</p>}
        </CardBody>
      </Card>
    </div>
  )
}
